import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';
import { useCallback } from 'react';
import { getOrCreateSessionToken, parseUserAgent, setSessionId } from '@/lib/sessionUtils';

export interface UserSession {
  id: string;
  device_type: 'desktop' | 'mobile' | 'tablet' | 'unknown';
  browser: string | null;
  browser_version: string | null;
  os: string | null;
  os_version: string | null;
  device_name: string | null;
  ip_address: string | null;
  city: string | null;
  country: string | null;
  country_code: string | null;
  is_current: boolean;
  created_at: string;
  last_active_at: string;
}


export function useActiveSessions() {
  const queryClient = useQueryClient();
  const { user, profile } = useAuth();

  // Fetch active sessions
  const {
    data: sessions,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['active-sessions', user?.id],
    queryFn: async (): Promise<UserSession[]> => {
      if (!user?.id) throw new Error('No user');

      const { data, error } = await supabase.rpc('get_user_sessions', {
        p_user_id: user.id,
      });

      if (error) throw error;
      return (data as UserSession[]) || [];
    },
    enabled: !!user?.id,
    staleTime: 30000, // Consider fresh for 30 seconds
  });

  // Create a new session (call on login)
  const createSession = useCallback(async () => {
    if (!user?.id || !profile?.organization_id) return null;

    const sessionToken = getOrCreateSessionToken();
    const parsed = parseUserAgent(navigator.userAgent);

    const { data, error } = await supabase.rpc('create_user_session', {
      p_user_id: user.id,
      p_organization_id: profile.organization_id,
      p_session_token: sessionToken,
      p_user_agent: navigator.userAgent,
      p_device_type: parsed.deviceType,
      p_browser: parsed.browser,
      p_browser_version: parsed.browserVersion,
      p_os: parsed.os,
      p_os_version: parsed.osVersion,
    });

    if (error) {
      // Session might already exist, try to update activity instead
      if (error.code === '23505') {
        // Unique violation
        await updateActivity();
        return null;
      }
      console.error('Failed to create session:', error);
      return null;
    }

    if (data) {
      setSessionId(data as string);
    }

    queryClient.invalidateQueries({ queryKey: ['active-sessions'] });
    return data as string;
  }, [user, profile, queryClient]);

  // Update session activity
  const updateActivity = useCallback(async () => {
    const sessionToken = getOrCreateSessionToken();
    await supabase.rpc('update_user_session_activity', {
      p_session_token: sessionToken,
    });
  }, []);

  // Revoke a specific session
  const revokeSession = useMutation({
    mutationFn: async (sessionId: string) => {
      if (!user?.id) throw new Error('No user');

      const { data, error } = await supabase.rpc('revoke_user_session', {
        p_user_id: user.id,
        p_session_id: sessionId,
      });

      if (error) throw error;
      return data as boolean;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-sessions'] });
    },
  });

  // Revoke all other sessions
  const revokeAllOtherSessions = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('No user');

    const sessionToken = getOrCreateSessionToken();
      const { data, error } = await supabase.rpc('revoke_all_other_sessions', {
        p_user_id: user.id,
        p_current_session_token: sessionToken,
      });

      if (error) throw error;
      return data as number;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['active-sessions'] });
    },
  });

  // Format session for display
  const formatSession = useCallback((session: UserSession): string => {
    const parts: string[] = [];

    if (session.browser) {
      parts.push(session.browser + (session.browser_version ? ` ${session.browser_version}` : ''));
    }

    if (session.os) {
      parts.push(session.os + (session.os_version ? ` ${session.os_version}` : ''));
    }

    return parts.join(' on ') || 'Unknown device';
  }, []);

  // Get relative time string
  const getRelativeTime = useCallback((dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

    return date.toLocaleDateString();
  }, []);

  return {
    sessions: sessions || [],
    isLoading,
    error,
    refetch,

    // Actions
    createSession,
    updateActivity,
    revokeSession,
    revokeAllOtherSessions,

    // Helpers
    formatSession,
    getRelativeTime,
  };
}
